/**
 * Tiny markdown → HTML renderer for chat assistant bubbles. Intentionally
 * minimal: bold, italic, inline code, fenced code, unordered/ordered lists,
 * paragraphs and line breaks. Tables and links are out of scope (would
 * require nested parsing — keep this dependency-free).
 *
 * Output MUST flow through `HtmlSanitizerService.sanitizeTrusted()` before
 * being bound to `[innerHTML]`.
 */
export function markdownToHtml(input: string): string {
  if (!input) return '';

  // Extract fenced code blocks first so their content is not transformed.
  const codeBlocks: string[] = [];
  let src = input.replace(/```([\s\S]*?)```/g, (_match, code: string) => {
    codeBlocks.push(code);
    return ` CODE${codeBlocks.length - 1} `;
  });

  // Escape HTML to prevent injection from AI output.
  src = escapeHtml(src);

  // Inline transforms — order matters (bold before italics so `**` doesn't
  // get eaten by single `*`).
  src = src.replace(/\*\*([^*\n]+?)\*\*/g, '<strong>$1</strong>');
  src = src.replace(/(^|[^*])\*([^*\n]+?)\*(?!\*)/g, '$1<em>$2</em>');
  src = src.replace(/`([^`\n]+?)`/g, '<code>$1</code>');

  // Block transforms — split into paragraph chunks separated by blank lines.
  const blocks = src.split(/\n{2,}/).map(b => b.trim()).filter(Boolean);
  const html = blocks.map(block => renderBlock(block)).join('\n');

  // Restore fenced code blocks.
  // eslint-disable-next-line no-control-regex -- intentional NUL sentinel delimiters around code-block placeholders
  return html.replace(/ CODE(\d+) /g, (_match, idxStr) => {
    const code = codeBlocks[Number(idxStr)] ?? '';
    return `<pre><code>${escapeHtml(code.replace(/^\n/, ''))}</code></pre>`;
  });
}

function renderBlock(block: string): string {
  const lines = block.split('\n');
  if (lines.every(l => /^\s*[-*]\s+/.test(l))) {
    const items = lines.map(l => l.replace(/^\s*[-*]\s+/, '').trim());
    return `<ul>${items.map(i => `<li>${i}</li>`).join('')}</ul>`;
  }
  if (lines.every(l => /^\s*\d+\.\s+/.test(l))) {
    const items = lines.map(l => l.replace(/^\s*\d+\.\s+/, '').trim());
    return `<ol>${items.map(i => `<li>${i}</li>`).join('')}</ol>`;
  }
  // Paragraph — single line breaks become <br/>.
  return `<p>${lines.join('<br/>')}</p>`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
